"use client";

import {
  PromptInputCommand,
  PromptInputCommandEmpty,
  PromptInputCommandGroup,
  PromptInputCommandItem,
  PromptInputCommandList,
  PromptInputTextarea,
  usePromptInputController,
  type PromptInputTextareaProps,
} from "@/components/ai-elements/prompt-input";
import { BotMessageSquareIcon } from "@/components/ui/bot-message-square";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import {
  filterAgentsByMentionQuery,
  findAgentMentionQuery,
  findAgentMentionTokenRange,
  type MentionableAgent,
} from "@/lib/agent-mention";
import {
  filterSkillCommands,
  findSkillCommandQuery,
  findSkillCommandRanges,
  formatPillSeparators,
  parseSkillCommandInput,
  type SkillCommand,
} from "@/lib/skill-commands";
import { cn } from "@/lib/utils";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";

export type PendingAgentMention = {
  agentId: string;
  nickname: string;
};

// Pill ranges: the confirmed `@mention` (if its token is still in the text)
// plus every recognised `/skill` command, in text order.
const findPillRanges = (text: string, confirmedNickname: string | null) => {
  const agentRange = confirmedNickname
    ? findAgentMentionTokenRange(text, { nickname: confirmedNickname })
    : null;
  const ranges = [...(agentRange ? [agentRange] : []), ...findSkillCommandRanges(text)];
  return ranges.sort((a, b) => a.start - b.start);
};

// ── Caret pixel-position helper ──────────────────────────────────────────────
// No existing caret-position utility in the repo (the textarea isn't
// contenteditable). Mirrors the textarea's box + typography into a hidden div
// so a marker span placed at the caret offset reports the caret's real
// on-screen position. Small local implementation, no new dependency.
const MIRRORED_STYLE_PROPERTIES = [
  "box-sizing",
  "width",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "font-style",
  "font-variant",
  "font-weight",
  "font-size",
  "line-height",
  "font-family",
  "text-align",
  "text-transform",
  "text-indent",
  "text-decoration",
  "letter-spacing",
  "word-spacing",
  "tab-size",
  "word-break",
];

function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  caretIndex: number
): { top: number; left: number; height: number } {
  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordWrap = "break-word";
  mirror.style.top = "0";
  mirror.style.left = "-9999px";

  for (const prop of MIRRORED_STYLE_PROPERTIES) {
    mirror.style.setProperty(prop, computed.getPropertyValue(prop));
  }

  mirror.textContent = textarea.value.slice(0, caretIndex);
  const marker = document.createElement("span");
  marker.textContent = textarea.value.slice(caretIndex) || ".";
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  const top = marker.offsetTop - textarea.scrollTop;
  const left = marker.offsetLeft - textarea.scrollLeft;
  const height = marker.offsetHeight || Number.parseInt(computed.lineHeight, 10) || 20;

  document.body.removeChild(mirror);

  return { top, left, height };
}

type MentionQueryState = { query: string; start: number; end: number };

export type AgentMentionTextareaProps = Omit<
  PromptInputTextareaProps,
  "onChange" | "onKeyDown" | "ref"
> & {
  /** Agents the user can @mention for a one-off, per-turn answer. */
  agents: MentionableAgent[];
  /** Owned by the caller; read at submit time to route this one turn. */
  pendingMentionRef: RefObject<PendingAgentMention | null>;
};

// A picked token reuses the whitespace already after it instead of adding a
// second space, which would widen the gap beside the highlight pill.
const withTrailingSpace = (token: string, after: string) =>
  /^\s/.test(after) ? token : `${token} `;

/**
 * Drop-in replacement for `PromptInputTextarea` that additionally detects an
 * in-progress `@Nickname` mention, shows a caret-anchored autocomplete
 * popover, and arms `pendingMentionRef` when the user confirms a pick.
 * The same popover lists `/skill-name` commands; a picked command stays in
 * the text, where the chat routes read it to force that container skill.
 * Focus never leaves the textarea — arrow keys/Enter/Tab/Escape are
 * intercepted here rather than handled by cmdk's own input-driven navigation.
 */
export function AgentMentionTextarea({
  agents,
  pendingMentionRef,
  ...textareaProps
}: AgentMentionTextareaProps) {
  const controller = usePromptInputController();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const pendingCaretRef = useRef<number | null>(null);

  const [mentionQuery, setMentionQuery] = useState<MentionQueryState | null>(null);
  const [skillQuery, setSkillQuery] = useState<MentionQueryState | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  // Tracks the confirmed nickname to drive the inline highlight overlay.
  const [confirmedNickname, setConfirmedNickname] = useState<string | null>(null);

  const filteredAgents = useMemo(
    () => (mentionQuery ? filterAgentsByMentionQuery(agents, mentionQuery.query) : []),
    [agents, mentionQuery]
  );
  const filteredSkills = useMemo(
    () => (skillQuery ? filterSkillCommands(skillQuery.query) : []),
    [skillQuery]
  );

  // Restore the caret after a confirmed mention splices text into the
  // controlled value (the DOM value updates a render after setInput).
  useEffect(() => {
    if (pendingCaretRef.current === null) return;
    const caret = pendingCaretRef.current;
    pendingCaretRef.current = null;
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(caret, caret);
    }
  }, [controller.textInput.value]);

  const detectMention = useCallback(
    (value: string, caret: number) => {
      const found = agents.length > 0 ? findAgentMentionQuery(value, caret) : null;
      const foundSkill = found ? null : findSkillCommandQuery(value, caret);
      const active = found ?? foundSkill;
      const previous = mentionQuery ?? skillQuery;
      if (active?.query !== previous?.query || active?.start !== previous?.start) {
        setActiveIndex(0);
      }
      setMentionQuery(found);
      setSkillQuery(foundSkill);

      if (active && textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        const caretPos = getCaretCoordinates(textareaRef.current, caret);
        setCoords({
          top: rect.top + caretPos.top + caretPos.height,
          left: rect.left + caretPos.left,
        });
      }

      // If the armed mention's token was edited or deleted, allow a new pick.
      const pending = pendingMentionRef.current;
      if (pending && !value.includes(`@${pending.nickname}`)) {
        pendingMentionRef.current = null;
        setConfirmedNickname(null);
      } else if (!pending && confirmedNickname) {
        // The ref was cleared externally (e.g. on send) — drop the highlight
        // so a later hand-typed "@Nickname", which wouldn't route, can't
        // inherit it.
        setConfirmedNickname(null);
      }
    },
    [agents, mentionQuery, skillQuery, pendingMentionRef, confirmedNickname]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = parseSkillCommandInput(e.currentTarget.value);
      const caret = e.currentTarget.selectionStart ?? value.length;
      detectMention(value, caret);
    },
    [detectMention]
  );

  const confirmMention = useCallback(
    (agent: MentionableAgent) => {
      if (!mentionQuery) return;

      const value = controller.textInput.value;
      const before = value.slice(0, mentionQuery.start);
      const after = value.slice(mentionQuery.end);
      const insertion = withTrailingSpace(`@${agent.nickname}`, after);

      pendingCaretRef.current = before.length + insertion.length + (/^\s/.test(after) ? 1 : 0);
      controller.textInput.setInput(`${before}${insertion}${after}`);

      // First confirmed mention wins for this turn (see disarm logic above).
      if (!pendingMentionRef.current) {
        pendingMentionRef.current = { agentId: agent.id, nickname: agent.nickname };
        setConfirmedNickname(agent.nickname);
      }

      setMentionQuery(null);
    },
    [controller, mentionQuery, pendingMentionRef, setConfirmedNickname]
  );

  const confirmSkill = useCallback(
    (skill: SkillCommand) => {
      if (!skillQuery) return;

      const value = controller.textInput.value;
      const before = value.slice(0, skillQuery.start);
      const after = value.slice(skillQuery.end);
      const insertion = withTrailingSpace(`/${skill.name}`, after);

      pendingCaretRef.current = before.length + insertion.length + (/^\s/.test(after) ? 1 : 0);
      controller.textInput.setInput(`${before}${insertion}${after}`);
      setSkillQuery(null);
    },
    [controller, skillQuery]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      const count = mentionQuery ? filteredAgents.length : skillQuery ? filteredSkills.length : 0;
      if (count === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % count);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + count) % count);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (mentionQuery) confirmMention(filteredAgents[activeIndex]);
        else confirmSkill(filteredSkills[activeIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setMentionQuery(null);
        setSkillQuery(null);
      }
    },
    [mentionQuery, skillQuery, filteredAgents, filteredSkills, activeIndex, confirmMention, confirmSkill]
  );

  // Keep overlay scroll in sync with the textarea.
  const syncOverlayScroll = useCallback(() => {
    if (overlayRef.current && textareaRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // A confirmed mention only highlights while its token is still in the text.
  // Programmatic resets (e.g. input cleared on send) leave `confirmedNickname`
  // armed but yield no range here; detectMention then drops the stale state on
  // the next user edit before it could wrongly highlight a hand-typed token.
  // Skill commands highlight whenever a recognised token is in the text — the
  // server reads the text itself, so what's highlighted is what gets forced.
  const text = controller.textInput.value;
  const highlightRanges = useMemo(
    () => findPillRanges(text, confirmedNickname),
    [text, confirmedNickname]
  );
  const displayText = formatPillSeparators(text, highlightRanges);
  const valueTransform = useMemo(
    () => ({
      format: (value: string) =>
        formatPillSeparators(value, findPillRanges(value, confirmedNickname)),
      parse: parseSkillCommandInput,
    }),
    [confirmedNickname]
  );
  const hasHighlight = highlightRanges.length > 0;

  // Re-sync after the controlled value updates (typing can scroll the textarea
  // before the overlay re-renders).
  useEffect(syncOverlayScroll, [syncOverlayScroll, text, highlightRanges]);

  const showMenu = Boolean(mentionQuery || (skillQuery && filteredSkills.length > 0));

  // cmdk only scrolls on its own keyboard handling, and focus stays in the
  // textarea here — so keep the arrow-key pick in view ourselves.
  const menuListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showMenu) return;
    menuListRef.current
      ?.querySelector(`[data-menu-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [showMenu, activeIndex, mentionQuery, skillQuery]);

  // Drive cmdk's own selection from activeIndex so its data-selected styling
  // never highlights a second item; hovering an item moves activeIndex too.
  const menuValues = skillQuery
    ? filteredSkills.map((skill) => `skill:${skill.name}`)
    : filteredAgents.map((agent) => `agent:${agent.id}`);

  return (
    <>
      {/* Wrapper anchors the highlight backdrop to the textarea area. */}
      <div className="relative w-full flex-1">
        {hasHighlight ? (
          // Backdrop mirror: same box + typography as the textarea, painted
          // beneath it (the textarea is position:relative while active). The
          // textarea's own glyphs turn transparent so the styled text below
          // shows through; caret and selection still belong to the textarea.
          <div
            ref={overlayRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap wrap-break-word px-3 py-2.5 text-base text-foreground md:text-sm"
          >
            {highlightRanges.map((range, index) => (
              <Fragment key={range.start}>
                {displayText.slice(index === 0 ? 0 : highlightRanges[index - 1].end, range.start)}
                {/* Negative margins balance the pill's padding so its glyphs,
                    following text and caret stay aligned with the textarea. */}
                <span
                  className={cn(
                    "-mx-1 box-decoration-clone rounded-md px-1 py-0.5",
                    text[range.start] === "/"
                      ? "bg-woods-red text-woods-red-foreground"
                      : "bg-foreground text-background"
                  )}
                >
                  {text.slice(range.start, range.end)}
                </span>
              </Fragment>
            ))}
            {displayText.slice(highlightRanges[highlightRanges.length - 1].end)}
            {/* A trailing newline collapses in a div but not in a textarea;
                the zero-width space keeps the mirror's last line box alive. */}
            {text.endsWith("\n") ? "\u200B" : null}
          </div>
        ) : null}
        <PromptInputTextarea
          {...textareaProps}
          ref={textareaRef}
          valueTransform={valueTransform}
          className={cn(
            hasHighlight && "relative text-transparent caret-foreground",
            textareaProps.className
          )}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onScroll={syncOverlayScroll}
        />
      </div>
      {showMenu && coords ? (
        <Popover
          open
          onOpenChange={(next) => {
            if (!next) {
              setMentionQuery(null);
              setSkillQuery(null);
            }
          }}
        >
          <PopoverAnchor asChild>
            <div
              style={{ position: "fixed", top: coords.top, left: coords.left, width: 1, height: 1 }}
            />
          </PopoverAnchor>
          <PopoverContent
            align="start"
            side="bottom"
            // Width tracks the longest nickname, capped so it never overflows
            // the viewport; longer names truncate on the item instead.
            className="w-auto min-w-44 max-w-[min(18rem,calc(100vw-2rem))] gap-0 rounded-xl p-1"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <PromptInputCommand
              shouldFilter={false}
              value={menuValues[activeIndex] ?? ""}
              onValueChange={(value) => {
                const index = menuValues.indexOf(value);
                if (index !== -1) setActiveIndex(index);
              }}
            >
              <PromptInputCommandList ref={menuListRef} className="max-h-48">
                {skillQuery ? (
                  <PromptInputCommandGroup className="p-0">
                    {filteredSkills.map((skill, index) => (
                      <PromptInputCommandItem
                        key={skill.name}
                        value={`skill:${skill.name}`}
                        data-menu-index={index}
                        className={cn(
                          "gap-1.5 rounded-lg px-2 py-1 text-sm",
                          index === activeIndex && "bg-accent text-accent-foreground"
                        )}
                        title={skill.description}
                        onMouseDown={(e) => {
                          // Keep focus in the textarea instead of the clicked item.
                          e.preventDefault();
                          confirmSkill(skill);
                        }}
                      >
                        <span className="min-w-0 truncate">/{skill.name}</span>
                      </PromptInputCommandItem>
                    ))}
                  </PromptInputCommandGroup>
                ) : filteredAgents.length === 0 ? (
                  <PromptInputCommandEmpty className="py-2 text-center text-muted-foreground text-xs">
                    No matching agents
                  </PromptInputCommandEmpty>
                ) : (
                  <PromptInputCommandGroup className="p-0">
                    {filteredAgents.map((agent, index) => (
                      <PromptInputCommandItem
                        key={agent.id}
                        value={`agent:${agent.id}`}
                        data-menu-index={index}
                        className={cn(
                          "gap-1.5 rounded-lg px-2 py-1 text-sm",
                          index === activeIndex && "bg-accent text-accent-foreground"
                        )}
                        onMouseDown={(e) => {
                          // Keep focus in the textarea instead of the clicked item.
                          e.preventDefault();
                          confirmMention(agent);
                        }}
                      >
                        <BotMessageSquareIcon aria-hidden="true" className="shrink-0" size={14} />
                        <span className="min-w-0 truncate" title={agent.nickname}>
                          {agent.nickname}
                        </span>
                      </PromptInputCommandItem>
                    ))}
                  </PromptInputCommandGroup>
                )}
              </PromptInputCommandList>
            </PromptInputCommand>
          </PopoverContent>
        </Popover>
      ) : null}
    </>
  );
}
